/**
 * Check if logged in user is part of an enterprise team with marketplace access
 *
 * For backend: Use getUserHasMarketplace
 */

import useAuthHasUserClass from '@apps/www/src/www/hooks/useAuthHasUserClass';
import useAuthSubscriptions from '@apps/www/src/www/hooks/useAuthSubscriptions';
import useIsLoggedIn from '@apps/www/src/www/hooks/useIsLoggedIn';
import config from '@pkgs/shared-client/config';
import SubscriptionTier from '@pkgs/shared/enums/SubscriptionTier';
import SubscriptionType from '@pkgs/shared/enums/SubscriptionType';
import UserClass from '@pkgs/shared/enums/UserClass';
import {
	doesEnterprisePlanHaveMarketplaceAccess,
	getSubscriptionTierFromPlanID,
} from '@pkgs/shared/plans';
import { useMemo } from 'react';

const useHasMarketplace = (): boolean => {
	const isLoggedIn = useIsLoggedIn();
	const isModerator = useAuthHasUserClass(UserClass.MODERATOR);
	const subscriptions = useAuthSubscriptions();

	// WARNING: If the logic changes here, also update `getUserHasMarketplace` for the backend

	const hasMarketplaceAccess = useMemo(() => {
		if (!isLoggedIn) {
			return false;
		}

		if (isModerator) {
			return true;
		}

		const enterpriseSubscription = subscriptions.find(
			(subscription) =>
				subscription.planID &&
				subscription.type === SubscriptionType.TEAM &&
				getSubscriptionTierFromPlanID(config, subscription.planID) ===
					SubscriptionTier.ENTERPRISE,
		);

		if (enterpriseSubscription && enterpriseSubscription.planID) {
			return doesEnterprisePlanHaveMarketplaceAccess(config, enterpriseSubscription.planID);
		}

		return false;
	}, [isLoggedIn, isModerator, subscriptions]);

	return hasMarketplaceAccess;
};

export default useHasMarketplace;
